import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { Sheet } from '@startuptools/ui/joy/Sheet';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Button } from '@startuptools/ui/joy/Button';
import { Link } from '@startuptools/ui/joy/Link';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { GqlCompany } from '../../../../../graphql/base-types.graphql';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Alert } from '@startuptools/ui/joy/Alert';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { OnReject } from './types';

interface FormData {
  startDate: DateTime | null;
}

export const TimeInBusiness = ({
  company,
  onReject,
}: {
  company: Pick<GqlCompany, 'foundationDate'>;
  onReject: OnReject;
}) => {
  const { stepNext } = useStepper();
  const { i18n } = useLingui();

  const foundationDate = DateTime.fromISO(company.foundationDate);
  const foundationOk = foundationDate > DateTime.now().minus({ years: 10 });

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      startDate: DateTime.fromISO(company.foundationDate),
    },
  });

  const onFormSubmit = handleSubmit(async ({ startDate }) => {
    if (startDate && startDate < DateTime.now().minus({ years: 10 })) {
      onReject({
        title: <Trans id="The company has been operating for over 10 years" />,
        description: <Trans id="the company has been in business for more than 10 years" />,
      });
    } else {
      await stepNext();
    }
  });

  return (
    <form onSubmit={onFormSubmit}>
      <Sheet
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        <Typography level="h4">
          <Trans id="How long has the company been operating?" />
        </Typography>

        <Alert variant="outlined" color="primary" sx={{ marginBottom: 2 }}>
          <Stack>
            <Typography level="body-sm">
              <Trans
                id="One of the requirements for issuing qualified employee stock options is that the company has been operating for a maximum of ten years. (After the end of the year when operations began + 10 calendar years).
               If the company has taken over operations from another company, the start date of that operation applies. For complete terms, <link>read more here</link>."
                components={{
                  link: (
                    <Link
                      href="https://startuptools.org/se/regler-for-personaloptioner/"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </Typography>
          </Stack>
        </Alert>

        {foundationOk ? (
          <Typography>
            <Trans
              id="Since the company was founded on {foundationDate}, it meets the requirement of having operated for a maximum of ten years."
              values={{
                foundationDate: <DateFormat locale={i18n.locale}>{foundationDate}</DateFormat>,
              }}
            />
          </Typography>
        ) : (
          <Sheet sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography>
              <Trans
                id="The company was founded more than ten years ago ({foundationDate}). When did operations begin?"
                values={{
                  foundationDate: foundationDate.toLocaleString(),
                }}
              />
            </Typography>
            <Sheet sx={{ maxWidth: '250px' }}>
              <Controller
                control={control}
                name="startDate"
                render={({ field, formState }) => (
                  <FormControl sx={{ maxWidth: 300 }}>
                    <DateInput {...field} />
                    {formState.errors.startDate && (
                      <FormHelperText>
                        <Trans id="Required field" />
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Sheet>
          </Sheet>
        )}

        <Sheet sx={{ pt: 2 }}>
          <Button type="submit">
            <Trans id="Continue" />
          </Button>
        </Sheet>
      </Sheet>
    </form>
  );
};
